/**
 * Générateur — Automatisme : reconnaître les solides : cube, pavé, cylindre,
 * prisme droit.
 * Attendu ID : 1115 | Niveau : 4ème | Domaine : Représentation de l'espace
 * Rubrique : AUTOMATISME (programme 2026, Représentation de l'espace 4e).
 * Labels (cartes exerciseur) :
 *   - Quel solide ?                  (QCM : un solide en perspective cavalière, quatre noms)
 *   - Retrouver le solide            (QCM : quatre solides A à D, lequel est un … ?)
 *   - Nommer les solides             (relier : solide A, B, C ↔ nom, avec des noms leurres)
 *   - Les solides : vrai ou faux ?   (V/F de pur rappel)
 *
 * ── Plan de cartes (feu vert donné d'avance, commande du 2026-09-10) ────────
 * Source : BO cycle 4 (2026), Représentation de l'espace 4e + grille P1-P16.
 * A. P1 : CM2 introduit (reconnaître, nommer, décrire cube, pavé, prisme,
 * cylindre, pyramide, cône, boule), 6e consolidé (cube, pavé, vocabulaire :
 * faces, arêtes, sommets), 5e étendu (prisme droit, cylindre : patrons,
 * représentation en perspective cavalière), 4e automatisme. P2 : sens
 * installés — un solide se reconnaît à ses faces (plates ou courbes), à ses
 * bases ; sens à distinguer — le pavé est un prisme droit particulier, le
 * cube un pavé particulier. Distracteurs : pyramide (une seule base, un
 * sommet), cône (base disque, un sommet), boule (aucune face plane). P3 :
 * aucun nombre, sauf faces / arêtes / sommets ≤ 15. P4 : sans objet.
 * B. P5 : perspective cavalière (arêtes cachées en pointillés), moteurs
 * svgSolides du 5e. P6 : aucun instrument. P7 : pas de volume ici (1117).
 * P8 : face, arête, sommet, base, face latérale, perspective cavalière.
 * C. P9 : vocabulaire des solides (6e), prisme et cylindre (5e). P10 :
 * constat. P11 : le cube est un pavé, le pavé est un prisme ; un cylindre n'a
 * ni sommet ni arête ; une pyramide n'est pas un prisme. P12 : rémanence
 * forte (CM2 → 6e → 5e).
 * D. P13 : erreurs → leurres : pyramide pour prisme (deux bases contre un
 * sommet), cône pour cylindre, cube pour pavé (arêtes inégales), boule. P14 :
 * aucun calcul. P15 : UNICITÉ — un cube est aussi un pavé droit et un prisme
 * droit, un pavé est aussi un prisme droit : ces noms ne sont JAMAIS proposés
 * comme leurres quand la figure les vérifie ; un relier ne mêle jamais cube,
 * pavé et prisme. P16 : QCM à noms distincts ; QCM sur figures à options
 * A-D dans l'ordre ; relier sources « Solide A » ↔ noms.
 * (V/F descriptif sur un solide retiré le 2026-09-10 : décrire n'est pas reconnaître.)
 * INVARIANTS : toute figure porte data-solides (type de chaque solide dessiné)
 * pour les solveurs ; V/F sans crochets, mixité 1-3 faux, familles exclusives.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeRelier,
    retirerCodesErreur,
} from '../../../utils';
import { imgElement } from '../../5eme/statistiques/svgStats';
import { paveNomme, cylindreDebout, prismeTri } from '../../5eme/espace/svgSolides';
import { prismePoly, pyramide, cone, boule, planche } from './svgSolides4e';

const ATTENDU_ID = 1115;
const opts = (label) => ({ niveau: '4eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_QUEL = 'Quel solide ?';
const L_RETROUVER = 'Retrouver le solide';
const L_NOMMER = 'Nommer les solides';
const L_RAPPEL = 'Les solides : vrai ou faux ?';

// ─── Types de solides ──────────────────────────────────────────────────────
const NOM = { cube: 'un cube', pave: 'un pavé droit', cylindre: 'un cylindre', prisme_tri: 'un prisme droit', prisme_poly: 'un prisme droit', pyramide: 'une pyramide', cone: 'un cône', boule: 'une boule' };
const NOMS_ATTENDU = ['un cube', 'un pavé droit', 'un cylindre', 'un prisme droit'];
const NOMS_LEURRES = ['une pyramide', 'un cône', 'une boule'];
const TYPES_ATTENDU = ['cube', 'pave', 'cylindre', 'prisme_tri', 'prisme_poly'];
/** Noms mathématiquement VRAIS pour un type (P15) : un cube est aussi un pavé et un prisme, un pavé est un prisme. */
const nomsVrais = (type) => (type === 'cube' ? ['un cube', 'un pavé droit', 'un prisme droit'] : type === 'pave' ? ['un pavé droit', 'un prisme droit'] : [NOM[type]]);
/** Caractéristiques : faces planes, sommets, arêtes, bases (null = sans objet). */
const CARAC = {
    cube: { faces: 6, sommets: 8, aretes: 12, bases: 'des carrés', facesNom: 'des carrés' },
    pave: { faces: 6, sommets: 8, aretes: 12, bases: 'des rectangles', facesNom: 'des rectangles' },
    cylindre: { faces: 2, sommets: 0, aretes: 0, bases: 'des disques', facesNom: null },
    prisme_tri: { faces: 5, sommets: 6, aretes: 9, bases: 'des triangles', facesNom: null },
    prisme_poly: { faces: null, sommets: null, aretes: null, bases: null, facesNom: null },       // fixé au tirage (n côtés)
};

/** Un solide dessiné : { type, svg, carac, n }. Le salt varie l'empreinte d'unicité. */
const dessinerSolide = (type, salt = randInt(1, 999)) => {
    if (type === 'cube') return { type, svg: paveNomme({ L: 3, H: 3, P: 3 }, { labels: false, salt }).svg, carac: CARAC.cube };
    if (type === 'pave') { const L = randomChoice([4, 5]); const H = randomChoice([2, 2.5]); const P = randomChoice([2.5, 3]); return { type, svg: paveNomme({ L, H, P }, { labels: false, salt }).svg, carac: CARAC.pave }; }
    if (type === 'cylindre') return { type, svg: cylindreDebout({ r: randomChoice([1.4, 1.7]), h: randomChoice([2.8, 3.4]), salt }).svg, carac: CARAC.cylindre };
    if (type === 'prisme_tri') return { type, svg: prismeTri({ salt }).svg, carac: CARAC.prisme_tri };
    if (type === 'prisme_poly') { const n = randomChoice([5, 6]); return { type, n, svg: prismePoly(n, salt), carac: { faces: n + 2, sommets: 2 * n, aretes: 3 * n, bases: n === 5 ? 'des pentagones' : 'des hexagones', facesNom: null } }; }
    if (type === 'pyramide') return { type, svg: pyramide(salt), carac: null };
    if (type === 'cone') return { type, svg: cone(salt), carac: null };
    return { type, svg: boule(salt), carac: null };
};
/** Un des quatre solides de l'attendu à parts égales, le prisme étant tantôt triangulaire, tantôt pentagonal ou hexagonal (revue 2026-09-10 : « prisme droit » sortait 40 % du temps). */
const tirerAttendu = () => { const t = randomChoice(['cube', 'pave', 'cylindre', 'prisme']); return t === 'prisme' ? randomChoice(['prisme_tri', 'prisme_poly']) : t; };

const figureSeule = (sol) => sol.svg.replace('<svg ', `<svg data-solides="${sol.type}" `);

// ─── Codes d'erreur (contenu.erreurs, lus par trouverErreurs) ──────────────
// Confusions classiques nommées par la paire (solide regardé, nom donné) : deux bases ou un sommet, pavé et cube.
const NOMS_DEUX_BASES = ['un cube', 'un pavé droit', 'un prisme droit'];
const codeConfusion = (type, nom) => {
    if ((nom === 'une pyramide' && ['cube', 'pave', 'prisme_tri', 'prisme_poly'].includes(type)) || (type === 'pyramide' && NOMS_DEUX_BASES.includes(nom))) return 'pointe_ou_deux_bases';
    if ((nom === 'un cône' && type === 'cylindre') || (type === 'cone' && nom === 'un cylindre')) return 'pointe_ou_deux_bases';
    if (nom === 'un cube' && type === 'pave') return 'pave_pris_pour_cube';
    return null;
};
// Codes des cibles leurres du relier, repris du catalogue commun.
const CODE_LEURRE = { pyramide: 'pointe_ou_deux_bases', cone: 'pointe_ou_deux_bases', cube: 'pave_pris_pour_cube' };

// ─── Anti-répétition ───────────────────────────────────────────────────────
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refQ = { v: null }; const refR = { v: null }; const refN = { v: null };

// ─── Carte 1 : quel solide ? (QCM à noms) ──────────────────────────────────

const construireQuel = () => {
    const sol = dessinerSolide(tirerAttendu());
    const bonne = NOM[sol.type];
    // leurres : jamais un nom mathématiquement vrai pour la figure (P15)
    const interdits = nomsVrais(sol.type);
    const pool = shuffle([...NOMS_ATTENDU, ...NOMS_LEURRES].filter((nm) => !interdits.includes(nm)));
    const options = shuffle([bonne, ...pool.slice(0, 3)]);
    const payload = makeQcm(options, bonne, '<p>Comment s\'appelle ce solide ?</p>', { ...opts(L_QUEL), elements_additionnels: imgElement(figureSeule(sol), 'medium') });
    payload.contenu = { ...payload.contenu, sousType: sol.type };
    payload._meta.params = { type: sol.type, n: sol.n, signature: `${sol.type}-${sol.n || ''}-${[...options].sort().join('|')}` };
    // Un nom faux n'a de code que s'il fait une confusion classique avec le solide dessiné.
    const erreurs = options.filter((o) => o !== bonne && codeConfusion(sol.type, o)).map((o) => ({ code: codeConfusion(sol.type, o), valeur: o }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genQuel = () => antiRepeat(construireQuel, refQ);

// ─── Carte 2 : retrouver le solide (QCM sur planche de quatre solides) ─────

/** Quatre types distincts dont `cible`, sans autre solide qui porte aussi le nom cherché (P15). */
const tirerPlanche = (cible) => {
    const nomCible = NOM[cible];
    const autres = shuffle(['cube', 'pave', 'cylindre', 'prisme_tri', 'prisme_poly', 'pyramide', 'cone', 'boule']
        .filter((t) => t !== cible && NOM[t] !== nomCible && !nomsVrais(t).includes(nomCible)));
    // pas deux prismes (tri et poly) ni deux « prisme droit » : un seul par planche
    const choisis = []; autres.forEach((t) => { if (choisis.length < 3 && !choisis.some((u) => NOM[u] === NOM[t])) choisis.push(t); });
    return shuffle([cible, ...choisis]);
};
const construireRetrouver = () => {
    const cible = tirerAttendu(); const types = tirerPlanche(cible);
    const lettres = ['A', 'B', 'C', 'D'];
    const solides = types.map((t, i) => ({ ...dessinerSolide(t), label: lettres[i] }));
    const bonne = solides.find((s) => s.type === cible).label;
    const payload = makeQcm(lettres, bonne, `<p>Parmi ces quatre solides, lequel est ${NOM[cible]} ?</p>`, { ...opts(L_RETROUVER), elements_additionnels: imgElement(planche(solides), 'large') });
    payload.contenu.choix = lettres; payload.contenu.bonne_reponse = bonne;
    payload.contenu = { ...payload.contenu, sousType: cible };
    payload._meta.params = { cible, types, signature: `${cible}-${types.join(',')}` };
    // La lettre choisie : le solide de cette lettre a reçu le nom demandé.
    const erreurs = solides.filter((s) => s.label !== bonne && codeConfusion(s.type, NOM[cible])).map((s) => ({ code: codeConfusion(s.type, NOM[cible]), valeur: s.label }));
    if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
    return payload;
};
const genRetrouver = () => antiRepeat(construireRetrouver, refR);

// ─── Carte 3 : nommer les solides (relier) ─────────────────────────────────

const construireNommer = () => {
    // trois solides de noms distincts et sans inclusion : un parmi cube/pavé, un cylindre, un prisme (tri ou poly)
    const types = shuffle([randomChoice(['cube', 'pave']), 'cylindre', randomChoice(['prisme_tri', 'prisme_poly'])]);
    const lettres = ['A', 'B', 'C'];
    const solides = types.map((t, i) => ({ ...dessinerSolide(t), label: lettres[i] }));
    const pairs = solides.map((s) => ({ source: `Solide ${s.label}`, target: NOM[s.type] }));
    // leurres : pyramide, cône, et « un cube » quand le pavé est montré (l'inverse serait vrai : un cube EST un pavé)
    const leurres = shuffle([{ target: 'une pyramide', code: 'pyramide' }, { target: 'un cône', code: 'cone' }, ...(types.includes('pave') ? [{ target: 'un cube', code: 'cube' }] : [])]).slice(0, 2);
    // consigne précisée (prof, 2026-09-10) : un pavé est aussi un prisme droit, le « nom le plus précis » lève l'ambiguïté
    const payload = makeRelier(pairs, '<p>Relie chaque solide au nom qui le décrit le plus précisément. Chaque nom ne sert qu\'une fois.</p>', { ...opts(L_NOMMER), elements_additionnels: imgElement(planche(solides), 'large'), cibles_leurres: leurres });
    payload._meta.params = { types, signature: types.slice().sort().join(',') + '|' + leurres.map((l) => l.code).sort().join(',') };
    payload.contenu = { ...payload.contenu, cibles_leurres: payload.contenu.cibles_leurres.map((l) => ({ ...l, code: CODE_LEURRE[l.code] })) };
    return payload;
};
const genNommer = () => antiRepeat(construireNommer, refN);

// ─── Carte 4 : les solides, vrai ou faux ? (pur rappel) ────────────────────

const POOL_RAPPEL = [
    // critères de RECONNAISSANCE (prof, 2026-09-10 : pas de description en nombres de faces ou de sommets)
    { l: 'Un solide dont les six faces sont des rectangles est un pavé droit.', v: true, f: 'pave' },
    { l: 'Un solide dont les faces sont des triangles qui se rejoignent en un sommet est un pavé droit.', v: false, f: 'pave', code: 'pointe_ou_deux_bases' },
    { l: 'Un pavé droit dont toutes les arêtes ont la même longueur est un cube.', v: true, f: 'cube' },
    { l: 'Un pavé droit dont les arêtes ont des longueurs différentes est un cube.', v: false, f: 'cube', code: 'pave_pris_pour_cube' },
    { l: 'Un solide avec deux disques superposables reliés par une surface courbe est un cylindre.', v: true, f: 'cylindre' },
    { l: 'Un solide avec un seul disque et un sommet est un cylindre.', v: false, f: 'cylindre', code: 'pointe_ou_deux_bases' },
    { l: 'Un solide avec deux bases polygonales superposables reliées par des rectangles est un prisme droit.', v: true, f: 'prisme' },
    { l: 'Un solide avec une seule base et des faces triangulaires qui se rejoignent en un sommet est un prisme droit.', v: false, f: 'prisme', code: 'pointe_ou_deux_bases' },
    { l: 'Un pavé droit est un prisme droit particulier : ses bases sont des rectangles.', v: true, f: 'inclusion' },
    { l: 'Un cylindre est un prisme droit particulier.', v: false, f: 'inclusion' },
    { l: 'Pour reconnaître un solide, on regarde la forme de ses faces et de ses bases.', v: true, f: 'methode' },
    { l: 'Pour reconnaître un solide, il suffit de regarder sa taille.', v: false, f: 'methode' },
    { l: 'En perspective cavalière, les arêtes cachées sont dessinées en pointillés.', v: true, f: 'cavaliere' },
    { l: 'En perspective cavalière, un cylindre se reconnaît à ses bases dessinées comme des ovales.', v: true, f: 'cavaliere_cyl' },
    { l: 'En perspective cavalière, les bases d\'un cylindre sont dessinées comme des rectangles.', v: false, f: 'cavaliere_cyl' },
    { l: 'En perspective cavalière, toutes les arêtes sont dessinées en traits pleins.', v: false, f: 'cavaliere' },
];

let dernieresLignesRappel1115 = [];

const genRappelVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const fausses = shuffle(POOL_RAPPEL.filter((it) => !it.v)); const vraies = shuffle(POOL_RAPPEL.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (new Set(lignes.map((it) => it.f)).size !== 4) continue;
        if (essai > 120) dernieresLignesRappel1115 = [];
        if (lignes.some((it) => dernieresLignesRappel1115.includes(it.l))) continue;
        dernieresLignesRappel1115 = lignes.map((it) => it.l);
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            '<p>Pour chaque affirmation sur les solides, dis si elle est vraie ou fausse.</p>',
            opts(L_RAPPEL),
        );
        payload._meta.params = { lignes: lignes.map((it) => POOL_RAPPEL.indexOf(it)) };
        const erreurs = lignes.filter((it) => !it.v && it.code).map((it) => ({ code: it.code, ligne: it.l }));
        if (erreurs.length) payload.contenu = { ...payload.contenu, erreurs };
        return payload;
    }
    throw new Error('gen_1115 : V/F rappel sans tirage après 160 essais');
};

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    quel_qcm_unique: genQuel,
    retrouver_qcm_unique: genRetrouver,
    nommer_relier: genNommer,
    rappel_tableau_vf: genRappelVf,
};

const FORMAT_LABELS = { qcm_unique: 'QCM', relier: 'Relier', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'quel', label: L_QUEL, formatsList: buildFormats('quel') },
    { id: 'retrouver', label: L_RETROUVER, formatsList: buildFormats('retrouver') },
    { id: 'nommer', label: L_NOMMER, formatsList: buildFormats('nommer') },
    { id: 'rappel', label: L_RAPPEL, formatsList: buildFormats('rappel') },
];

// Codes retirés le 03/10/2026 après la lecture à l'aveugle (docs/kit/REGLES_EQUIPE.md) : le nom ne
// décrit pas le choix construit, ou deux erreurs donnent le même nombre, ou le choix est une figure
// que la lecture n'a pas pu vérifier. Le choix reste proposé, il part sans nom.
const CODES_RETIRES = {
    retrouver_qcm_unique: ['pave_pris_pour_cube', 'pointe_ou_deux_bases'],
};

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    retirerCodesErreur(payload, CODES_RETIRES[key]);
    payload._meta.generatorKey = key;
    return payload;
};

export const _internals = { NOM, nomsVrais, CARAC, dessinerSolide, tirerPlanche, POOL_RAPPEL };
